// Forgot password, self-serve, for everyone: test-takers and staff alike. /forgot-password
// asks for the email and shows the one answer the API gives whoever the email belongs to;
// /reset-password opens from the emailed link, with the token in the URL fragment (never sent
// to a server, a log or a Referer), checks it, and takes the new password. The API signs that
// sign-in out everywhere, disconnects its AI apps and starts no session, so the person signs
// in next: takers on /sign-in, staff on /admin, authenticator code and all.
//
// Both pages exist only while email goes out (GET /v1/config): with it off, the sign-in pages
// don't link here and this page says so instead of promising an email that can't be sent.

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Page } from "../components/Page";
import { ApiError, api, type ResetKind } from "../lib/api";
import { forgetPersonSession } from "../lib/person";
import { useEmailEnabled } from "../lib/useEmailEnabled";

export const SENT = "If an account exists for that email, we've sent a link to reset your password.";
export const SENT_HINT = "Check your spam folder too. The link works for an hour.";
export const EMAIL_OFF = "Password resets by email are off right now.";
export const EMAIL_OFF_HINT = "Test-takers: open your results on the device you took it on and choose “Save your results”. Staff: ask an owner for a new sign-in link.";
const TRY_AGAIN = "We couldn't send the email. Try again.";
export const CHECK_FAILED = "We couldn't check this link just now.";

/** What a bad link gets told, by the API's reason. */
const LINK_PROBLEMS: Record<string, string> = {
  reset_link_expired: "This reset link has expired.",
  reset_link_used: "This reset link was already used, or the password was already reset with another link.",
  reset_link_invalid: "This reset link isn't right.",
};
const linkProblem = (err: unknown): string | null =>
  err instanceof ApiError && err.status === 404 ? (LINK_PROBLEMS[err.code] ?? LINK_PROBLEMS.reset_link_invalid!) : null;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordPage() {
  const emailId = useId();
  const emailEnabled = useEmailEnabled();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!EMAIL.test(email.trim())) return setError("Enter your email address.");
    setBusy(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? err.message : TRY_AGAIN);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <h1 className="heading mt-10 text-[30px] leading-[1.1]">Reset your password</h1>
      {emailEnabled === null ? (
        <p role="status" className="mt-6 text-muted">
          One moment…
        </p>
      ) : !emailEnabled ? (
        <div role="status" className="mt-6 rounded-[16px] border border-line bg-panel p-4 text-[16px] leading-snug">
          <p>{EMAIL_OFF}</p>
          <p className="mt-2 text-[14px] text-muted">{EMAIL_OFF_HINT}</p>
        </div>
      ) : sent ? (
        <div role="status" className="mt-6 rounded-[16px] border border-line bg-panel p-4 text-[16px] leading-snug">
          <p>{SENT}</p>
          <p className="mt-2 text-[14px] text-muted">{SENT_HINT}</p>
        </div>
      ) : (
        <>
          <p className="mt-3 text-[15px] text-muted">Enter the email you sign in with and we'll send you a link to choose a new one.</p>
          <form className="mt-6 flex flex-col gap-3" onSubmit={submit} noValidate>
            <label htmlFor={emailId} className="text-[15px] font-semibold">
              Email
            </label>
            <input
              id={emailId}
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="off"
              spellCheck={false}
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="btn btn-primary mt-2 w-full" disabled={busy}>
              {busy ? "Sending…" : "Email me a reset link"}
            </button>
            {error ? (
              <p role="alert" className="text-[15px] text-danger">
                {error}
              </p>
            ) : null}
          </form>
        </>
      )}
      <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 text-[15px] text-muted">
        <p>
          <Link to="/sign-in">Back to sign in</Link>
        </p>
        <p>
          Gifts Profile staff? The same link resets your staff password. Passkeys and sign-in links are on the{" "}
          <Link to="/admin">admin sign-in</Link>.
        </p>
      </div>
    </Page>
  );
}

/** The token from the address bar (left there until the link is known to work: see below). */
const tokenFromUrl = () => window.location.hash.replace(/^#/, "");

/** Takes the token out of the address bar, so it doesn't linger anywhere once it's done its job. */
function stripTokenFromUrl() {
  if (window.location.hash) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
}

type ResetState =
  | { step: "checking" }
  | { step: "bad"; message: string }
  /** The check didn't get an answer (network, a 429): the link may well be fine, so it can be tried again. */
  | { step: "retry"; message: string }
  | { step: "form"; kind: ResetKind; minPasswordLength: number }
  | { step: "done"; kind: ResetKind };

export function ResetPasswordPage() {
  const passwordId = useId();
  const token = useRef<string>("");
  const [state, setState] = useState<ResetState>({ step: "checking" });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = useRef(true);

  // Asks the API whether the link is good. The token stays in the address bar until the
  // answer is in: a reload while it's still there (after a network blip, say) keeps the
  // link; it's taken out as soon as the link has been checked, good or bad.
  const check = () => {
    setState({ step: "checking" });
    api
      .checkPasswordReset(token.current)
      .then((r) => {
        if (!live.current) return;
        stripTokenFromUrl();
        setState({ step: "form", kind: r.kind, minPasswordLength: r.minPasswordLength });
      })
      .catch((err) => {
        if (!live.current) return;
        const problem = linkProblem(err);
        if (problem) {
          stripTokenFromUrl();
          setState({ step: "bad", message: problem });
        } else {
          setState({ step: "retry", message: err instanceof ApiError && err.status === 429 ? err.message : CHECK_FAILED });
        }
      });
  };

  useEffect(() => {
    live.current = true;
    // Belt and braces with the API's Referrer-Policy header: nothing this page loads or
    // links to learns where the browser came from.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    token.current = tokenFromUrl();
    if (!token.current) setState({ step: "bad", message: LINK_PROBLEMS.reset_link_invalid! });
    else check();
    return () => {
      live.current = false;
      meta.remove();
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.step !== "form") return;
    setError(null);
    if (password.length < state.minPasswordLength) return setError(`Use at least ${state.minPasswordLength} characters.`);
    setBusy(true);
    try {
      const { kind } = await api.resetPassword(token.current, password);
      token.current = "";
      setPassword("");
      // The API signed this sign-in out everywhere; this browser's copy of a taker session goes too.
      if (kind === "taker") forgetPersonSession();
      setState({ step: "done", kind });
    } catch (err) {
      const problem = linkProblem(err);
      if (problem) setState({ step: "bad", message: problem });
      else setError(err instanceof ApiError && err.status !== 500 && err.status !== 0 ? err.message : "We couldn't save your password. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const signInPath = (kind: ResetKind) => (kind === "staff" ? "/admin" : "/sign-in");

  return (
    <Page>
      {state.step === "checking" ? (
        <p role="status" className="mt-16 text-muted">
          Checking your link…
        </p>
      ) : state.step === "retry" ? (
        <>
          <h1 className="heading mt-10 text-[30px] leading-[1.1]">Let's try that again</h1>
          <p role="alert" className="mt-3 text-[16px] leading-snug">
            {state.message}
          </p>
          <p className="mt-6">
            <button type="button" className="btn btn-primary min-h-11 px-5" onClick={check}>
              Try again
            </button>
          </p>
        </>
      ) : state.step === "bad" ? (
        <>
          <h1 className="heading mt-10 text-[30px] leading-[1.1]">This link won't work</h1>
          <p role="alert" className="mt-3 text-[16px] leading-snug">
            {state.message}
          </p>
          <p className="mt-6">
            <Link to="/forgot-password" className="btn btn-primary inline-flex min-h-11 px-5 no-underline">
              Ask for a new link
            </Link>
          </p>
        </>
      ) : state.step === "done" ? (
        <>
          <h1 className="heading mt-10 text-[30px] leading-[1.1]">Your password is changed</h1>
          <p role="status" className="mt-3 text-[16px] leading-snug">
            Every device that was signed in has been signed out, and any connected apps were disconnected. Sign in with your new password.
          </p>
          <p className="mt-6">
            <Link to={signInPath(state.kind)} className="btn btn-primary inline-flex min-h-11 px-5 no-underline">
              Sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <p className="eyebrow mt-10 text-accent-text">{state.kind === "staff" ? "Staff sign-in" : "Your profiles"}</p>
          <h1 className="heading mt-2 text-[30px] leading-[1.1]">Choose a new password</h1>
          <p className="mt-3 text-[15px] text-muted">
            At least {state.minPasswordLength} characters. Signing in elsewhere will stop until you sign in again, and any connected apps will be disconnected.
          </p>
          <form className="mt-6 flex flex-col gap-3" onSubmit={submit} noValidate>
            <label htmlFor={passwordId} className="text-[15px] font-semibold">
              New password
            </label>
            <input
              id={passwordId}
              type="password"
              name="new-password"
              autoComplete="new-password"
              minLength={state.minPasswordLength}
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className="btn btn-primary mt-2 w-full" disabled={busy}>
              {busy ? "Saving…" : "Save password"}
            </button>
            {error ? (
              <p role="alert" className="text-[15px] text-danger">
                {error}
              </p>
            ) : null}
          </form>
        </>
      )}
    </Page>
  );
}
